import React from 'react';
import { Paper, CircularProgress, Alert, useTheme } from '@mui/material';
import { BaseChart } from '@personal-dashboard/ui';
import { useQuery, gql } from '@apollo/client';
import dayjs from 'dayjs';

const GET_LANGUAGE_STATS = gql`
  query GetLanguageStats {
    getLanguageProgress(language: "swedish") {
      id
      setId
      wordId
      mastery
      updatedAt
    }
  }
`;

interface LanguageProgressWidgetProps {
  daysCount: number;
}

const LanguageProgressWidget: React.FC<LanguageProgressWidgetProps> = ({ daysCount }) => {
  const theme = useTheme();

  const { data, loading, error } = useQuery(GET_LANGUAGE_STATS);

  if (loading) {
    return (
      <Paper sx={{ p: 3, height: 400, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={28} />
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 3, height: 400, borderRadius: 2 }}>
        <Alert severity="error">{error.message}</Alert>
      </Paper>
    );
  }

  const weekDays = Array.from({ length: daysCount }, (_, i) =>
    dayjs().subtract(daysCount - 1 - i, 'day').startOf('day')
  );

  const datesFormatted = weekDays.map(d => d.format('DD/MM'));

  const langProgress = data?.getLanguageProgress || [];

  const languagePracticedCount = weekDays.map(day => {
    const dayEnd = day.endOf('day');
    return langProgress.filter((p: any) => {
      const pDate = dayjs(isNaN(Number(p.updatedAt)) ? p.updatedAt : Number(p.updatedAt));
      return pDate.isBefore(dayEnd);
    }).length;
  });

  const languageMasteredCount = weekDays.map(day => {
    const dayEnd = day.endOf('day');
    return langProgress.filter((p: any) => {
      const pDate = dayjs(isNaN(Number(p.updatedAt)) ? p.updatedAt : Number(p.updatedAt));
      return p.mastery === 5 && pDate.isBefore(dayEnd);
    }).length;
  });

  const languageOption = {
    title: {
      text: 'Swedish Vocabulary Progress',
      left: 'center',
      textStyle: { fontSize: 16, fontWeight: 500, color: theme.palette.text.primary }
    },
    tooltip: {
      trigger: 'axis',
      backgroundColor: theme.palette.background.paper,
      textStyle: { color: theme.palette.text.primary },
      borderWidth: 1,
      borderColor: theme.palette.divider
    },
    legend: {
      data: ['Practiced', 'Mastered'],
      bottom: 0,
      textStyle: { color: theme.palette.text.secondary }
    },
    grid: { left: '3%', right: '4%', bottom: '15%', containLabel: true },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: datesFormatted,
      axisLine: { lineStyle: { color: theme.palette.divider } },
      axisLabel: { color: theme.palette.text.secondary }
    },
    yAxis: {
      type: 'value',
      name: 'Words',
      axisLabel: { color: theme.palette.text.secondary },
      nameTextStyle: { color: theme.palette.text.secondary },
      splitLine: { lineStyle: { color: theme.palette.divider, type: 'dashed' } }
    },
    series: [
      {
        name: 'Practiced',
        type: 'line',
        smooth: true,
        data: languagePracticedCount,
        symbolSize: 6,
        itemStyle: { color: theme.palette.primary.main },
        lineStyle: { width: 3 },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [
              { offset: 0, color: theme.palette.primary.main + '33' },
              { offset: 1, color: theme.palette.primary.main + '00' }
            ]
          }
        }
      },
      {
        name: 'Mastered',
        type: 'line',
        smooth: true,
        data: languageMasteredCount,
        symbolSize: 6,
        itemStyle: { color: theme.palette.success.main },
        lineStyle: { width: 3 },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [
              { offset: 0, color: theme.palette.success.main + '33' },
              { offset: 1, color: theme.palette.success.main + '00' }
            ]
          }
        }
      }
    ]
  };

  return (
    <Paper sx={{ p: 3, height: 400, borderRadius: 2 }}>
      <BaseChart option={languageOption} />
    </Paper>
  );
};

export default LanguageProgressWidget;
